import React, { useState, useContext, Fragment } from 'react';
import { TextField, Button, Grid, CardMedia, Menu, MenuItem, Box, Container, Typography } from '@mui/material';
import logo from '../assets/images/logo/logo_total.png'
import {
	GoogleReCaptchaProvider,
	GoogleReCaptcha,
	useGoogleReCaptcha
} from "react-google-recaptcha-v3";
import { login, validateMfa, resendMfa } from '../api/Login'
import { mfaErrorKey, mfaNeedsRestart } from '../mfaErrors'
import { toast } from 'react-toastify'
import { AppContext } from '../context/AppContext'
import { LoadingButton } from '@mui/lab';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import jwt from 'jwt-decode';

const keyCaptcha = process.env.REACT_APP_RECAPTCHA_KEY

function LoginForm() {

	// shouldUnregister: el campo del código y los del login no conviven en pantalla;
	// sin esto el que está oculto seguiría bloqueando el submit por su required.
	const { register, handleSubmit, formState: { errors } } = useForm({ shouldUnregister: true });

	const { executeRecaptcha } = useGoogleReCaptcha();
	const { hydrate, setAuth } = useContext(AppContext)
	let navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const [loading, setLoading] = useState(false)
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	// Con temp_token la vista pasa al paso del código; volver a null regresa al login.
	// ponytail: el temp_token vive solo en memoria, así que recargar en este paso
	// devuelve al login — correcto, porque el código caduca en minutos.
	const [tempToken, setTempToken] = useState(null)
	const [resending, setResending] = useState(false)

	const handleMfaError = (err) => {
		toast.error(t(mfaErrorKey(err)))
		if (mfaNeedsRestart(err)) {
			setTempToken(null)
		}
	}

	const changeLanguage = lng => {
		i18n.changeLanguage(lng);
		setAnchorEl(null);
	};

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const getUser = (uuid) => {
		hydrate().then(() => {
			toast.success(t('session.success'))
			setLoading(false)
			navigate('/dashboard/services');
		}).catch(() => {
			setLoading(false)
			toast.error(t('error'))
		})
	};

	const submit = async (data) => {
		setLoading(true)
		try {
			const token = await executeRecaptcha('login');
			login({
				email: data.email,
				password: data.password,
				token: token
			}).then(res => {
				// El back no entrega sesión aquí: manda un temp_token y espera
				// el código en users/mfa/validate/.
				if (res?.data?.mfa_required) {
					setTempToken(res.data.temp_token)
					setLoading(false)
					toast.info(t('mfa.sent'))
					return
				}
				let userUuid = null;
				if (res?.data?.token) {
					setAuth(res.data.token)
					userUuid = jwt(res.data.token)?.user_uuid;
				}
				getUser(userUuid)
			}).catch(err => {
				setLoading(false)
				toast.error(t('session.error'))
			})
		} catch (e) {
			toast.error(t('error'))
			setLoading(false)
		}
	}

	const submitCode = async (data) => {
		setLoading(true)
		try {
			const res = await validateMfa({ temp_token: tempToken, code: data.code })
			if (res?.data?.token) {
				setAuth(res.data.token)
			}
			getUser()
		} catch (err) {
			setLoading(false)
			handleMfaError(err)
		}
	}

	const resend = async () => {
		setResending(true)
		try {
			await resendMfa({ temp_token: tempToken })
			toast.success(t('mfa.resent'))
		} catch (err) {
			handleMfaError(err)
		} finally {
			setResending(false)
		}
	}

	return (
		<Fragment>
			<Container component="main" maxWidth="xs">
				<Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh', justifyContent:'center'}}>
					<CardMedia
						component="img"
						image={logo}
						alt="primary smartquick logo"
					/>
					{/* Las key evitan que React reutilice el input de la contraseña
					    para el campo del código y arrastre su valor. */}
					{tempToken ? (
						<Box key="mfa" component="form" onSubmit={handleSubmit(submitCode)} noValidate sx={{ mt: 1 }}>
							<Typography variant="body2" sx={{ mb: 1 }}>
								{t('mfa.sent')}
							</Typography>
							<TextField
								required
								fullWidth
								error={errors.code ? true : false}
								id="code"
								label={t('mfa.code')}
								name="code"
								type="text"
								autoComplete="one-time-code"
								inputProps={{ inputMode: 'numeric' }}
								color='secondary'
								autoFocus
								defaultValue=""
								{...register("code", { required: true })}
								helperText={errors.code ? t('errors.required') : ''}
							/>
							<LoadingButton
								loading={loading}
								type="submit"
								fullWidth
								variant="contained"
								sx={{ mt: 3, mb: 1 }}
							>
								{t('mfa.validate')}
							</LoadingButton>
							<LoadingButton
								loading={resending}
								onClick={resend}
								fullWidth
								variant="text"
								sx={{ mb: 2 }}
							>
								{t('mfa.resend')}
							</LoadingButton>
						</Box>
					) : (
						<Box key="login" component="form" onSubmit={handleSubmit(submit)} noValidate sx={{ mt: 1 }}>
							<TextField
								required
								fullWidth
								error={errors.email ? true : false}
								id="email"
								label={t('field.email')}
								name="email"
								type="email"
								autoComplete="email"
								color='secondary'
								autoFocus
								{...register("email", {required: true})}
								helperText={errors.email ? t('errors.required') : ''}
							/>
							<TextField
								margin="normal"
								required
								fullWidth
								error={errors.password ? true : false}
								name="password"
								label={t('field.password')}
								type="password"
								id="password"
								autoComplete="current-password"
								color='secondary'
								{...register("password", {required: true})}
								helperText={errors.password ? t('errors.required') : ''}
							/>
							<LoadingButton
								loading={loading}
								type="submit"
								fullWidth
								variant="contained"
								sx={{ mt: 3, mb: 2 }}
							>
								{t('btn.login')}
							</LoadingButton>
						</Box>
					)}
					<Grid container>
						<Grid item sx={{ m: 0 }} style={{ marginLeft: 'auto' }}>
							<Button
								id="demo-positioned-button"
								aria-controls="demo-positioned-menu"
								aria-haspopup="true"
								aria-expanded={open ? 'true' : undefined}
								onClick={handleClick}
								variant="text"
								startIcon={<TranslateIcon />}
							>
								{t('btn.language')}
							</Button>
							<Menu
								id="demo-positioned-menu"
								aria-labelledby="demo-positioned-button"
								anchorEl={anchorEl}
								open={open}
								onClose={() => setAnchorEl(null)}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
							>
								<MenuItem onClick={() => changeLanguage('en')}>English</MenuItem>
								<MenuItem onClick={() => changeLanguage('es')}>Español</MenuItem>
							</Menu>
						</Grid>
					</Grid>
				</Box>
			</Container>
			<GoogleReCaptcha />
		</Fragment>
	);
}

const Recaptcha = () => {
	const { i18n } = useTranslation();
	return (
		<GoogleReCaptchaProvider
			language={i18n.language}
			reCaptchaKey={keyCaptcha}
		>
			<LoginForm />
		</GoogleReCaptchaProvider>
	);
};

export default Recaptcha
